import { useEffect } from 'react';
import { MarkdownContent } from '../marketing/MarkdownContent';

interface PolicyModalProps {
  title: string;
  content: string;
  onClose: () => void;
}

export function PolicyModal({ title, content, onClose }: PolicyModalProps): JSX.Element {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="policy-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" aria-hidden="true" />

      {/* Panel */}
      <div
        className="relative z-10 w-full max-w-3xl max-h-[85vh] flex flex-col rounded-[var(--landing-radius)] bg-[var(--landing-surface)] border border-[var(--landing-border)]"
        style={{ boxShadow: 'var(--landing-shadow)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--landing-border)] flex-shrink-0">
          <h2 className="text-lg font-semibold text-[var(--landing-text-primary)]">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            data-testid="policy-modal-close"
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)] hover:bg-[var(--landing-border)] transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M3.293 3.293a1 1 0 011.414 0L8 6.586l3.293-3.293a1 1 0 111.414 1.414L9.414 8l3.293 3.293a1 1 0 01-1.414 1.414L8 9.414l-3.293 3.293a1 1 0 01-1.414-1.414L6.586 8 3.293 4.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-6 py-5">
          <MarkdownContent>{content}</MarkdownContent>
        </div>
      </div>
    </div>
  );
}
